/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, OnInit, TemplateRef, ViewChild, inject} from '@angular/core';
import {UserManagementService} from '../../../../services/user-management.service';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ValueGetterParams,
} from 'ag-grid-community'; //NOSONAR
import {getIxTheme} from '@siemens/ix-aggrid';
import * as agGrid from 'ag-grid-community';
import {TranslateService} from '@ngx-translate/core';
import {EditCellRendererComponent} from './edit-cell-renderer.component';
import {User} from '../../../../models/User';
import {StatusCellRendererComponent} from './status-cell-renderer.component';
import {WindowSizeService} from '../../../../services/window-size.service';
import {UserService} from '../../../../services/user.service';
import {GeneralDataHandlingService} from '../../../../services/general.data-handling.service';
import {Role} from '../../../../models/Role';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {Router} from '@angular/router';

@Component({
  selector: 'app-list-user',
  templateUrl: './list-user.component.html',
  styleUrl: './list-user.component.scss',
  standalone: false,
})
export class ListUserComponent implements OnInit {
  private readonly userManagementService = inject(UserManagementService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);

  @ViewChild('modalUserDeletion', {read: TemplateRef})
  modalUserDeletion!: TemplateRef<never>;

  gridApi: GridApi | null = null;
  loading = true;
  initialUserList: User[] = [];
  userList: User[] = [];

  protected readonly WindowSizeService = WindowSizeService;

  columnDefs: ColDef[] = [
    {
      field: 'firstName',
      headerValueGetter: () => this.translateService.instant('um.first_name'),
      sortable: true,
      comparator: this.compareFunction,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'lastName',
      headerValueGetter: () => this.translateService.instant('um.last_name'),
      sortable: true,
      comparator: this.compareFunction,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'email',
      headerValueGetter: () => this.translateService.instant('um.email'),
      sortable: true,
      comparator: this.compareFunction,
      minWidth: 200,
      flex: 2,
    },
    {
      field: 'roles',
      headerValueGetter: () => this.translateService.instant('um.roles_title'),
      flex: 3,
      minWidth: 150,
      sortable: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: 'normal',
        lineHeight: '1.5rem',
        alignContent: 'center',
      },
      valueGetter: this.roleValueGetter.bind(this),
    },
    {
      field: 'status',
      headerName: '',
      minWidth: 100,
      flex: 1,
      cellClass: 'status-cell',
      cellRenderer: StatusCellRendererComponent,
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.2,
      minWidth: 100,
      sortable: false,
      cellRenderer: EditCellRendererComponent,
    },
  ];

  suggestions: string[] = [];
  availableRoles: Role[] = [];
  gridOptions?: GridOptions = {};

  constructor() {
    this.gridOptions = {
      theme: getIxTheme(agGrid),
      context: {
        listUserComponent: this,
      },
    };
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  async ngOnInit() {
    this.loading = true;
    this.translateService.onLangChange.subscribe(() => {
      if (this.gridApi) {
        this.gridApi.refreshHeader();
        this.gridApi.refreshCells({force: true});
      }
    });
    await this.loadRoleList();
    await this.loadUserList();
    this.loading = false;
  }

  compareFunction(valueA: string, valueB: string): number {
    if (!valueA) {
      return valueB ? -1 : 0;
    }
    if (!valueB) {
      return 1;
    }
    return valueA.localeCompare(valueB, undefined, {sensitivity: 'base'});
  }

  async loadRoleList() {
    this.userService.availableRolesChange.subscribe((roles) => {
      if (roles) {
        this.suggestions = [];
        for (const role of roles) {
          this.suggestions.push(
            this.translateService.instant('um.roles.' + role.label),
          );
        }
      }
    });
  }

  async loadUserList() {
    this.initialUserList = await this.userManagementService.loadUserList();
    this.userList = JSON.parse(JSON.stringify(this.initialUserList));
  }

  onFilterChange(event: CustomEvent) {
    const tokens = event.detail.tokens;
    if (tokens.length > 0) {
      this.userList = this.initialUserList.filter(
        (user) =>
          user.roles &&
          user.roles.some((role) => {
            const roleWithoutOrg =
              GeneralDataHandlingService.returnRoleLabel(role);
            const translatedRole = this.translateService.instant(
              'um.roles.' + roleWithoutOrg,
            );
            return tokens.includes(translatedRole);
          }),
      );
    } else {
      this.userList = this.initialUserList;
    }
  }

  roleValueGetter(params: ValueGetterParams) {
    const translatedRoles = [];
    if (params.data.roles && params.data.roles.length > 0) {
      for (const role of params.data.roles) {
        translatedRoles.push(
          this.translateService.instant(
            'um.roles.' + GeneralDataHandlingService.returnRoleLabel(role),
          ),
        );
      }
    }
    return translatedRoles.join(', ');
  }

  async onDeleteClick(user: User) {
    const instance = await this.modalService.open({
      content: this.modalUserDeletion,
      data: user,
    });

    instance.onClose.on(async (event: 'okay' | undefined) => {
      if (event && event === 'okay') {
        this.userManagementService
          .deleteUser(user.id)
          .then(() => {
            this.toastService.show({
              type: 'success',
              message: this.translateService.instant('um.delete.success', {
                firstName: user.firstName,
                lastName: user.lastName,
              }),
            });
            this.ngOnInit();
          })
          .catch((error) => {
            this.toastService.show({
              type: 'error',
              message: this.translateService.instant('um.delete.error', {
                firstName: user.firstName,
                lastName: user.lastName,
                error: error.message,
              }),
            });
          });
      }
    });
  }
}
